import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import api, { Lane, LanePriceHistoryItem, VinInventoryItem } from '../lib/api'

function centsToDollars(value?: number | null): string {
  if (typeof value !== 'number') return '-'
  return `$${(value / 100).toFixed(2)}`
}

export default function LaneDetailPage() {
  const { laneId = '' } = useParams()
  const navigate = useNavigate()

  const [item, setItem] = useState<Lane | null>(null)
  const [history, setHistory] = useState<LanePriceHistoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [vinItems, setVinItems] = useState<VinInventoryItem[]>([])

  const [notes, setNotes] = useState('')
  const [targetCustomerPayCents, setTargetCustomerPayCents] = useState('')
  const [targetCarrierPayCents, setTargetCarrierPayCents] = useState('')
  const [status, setStatus] = useState('ACTIVE')

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const [laneRes, historyRes] = await Promise.all([
          api.lanes.get(laneId),
          api.lanes.history(laneId).catch(() => null),
        ])

        const lane = laneRes.data.lane
        setItem(lane)
        setNotes(lane.notes ?? '')
        setStatus(lane.status ?? 'ACTIVE')
        setTargetCustomerPayCents(lane.targetCustomerPayCents == null ? '' : String(lane.targetCustomerPayCents))
        setTargetCarrierPayCents(lane.targetCarrierPayCents == null ? '' : String(lane.targetCarrierPayCents))
        setHistory(historyRes?.data.history ?? [])
        const vinResponse = await api.vinInventory.list({ laneId, limit: 10 })
        setVinItems(vinResponse.data)
      } catch (err: any) {
        setError(err?.response?.data?.error ?? 'Failed to load lane')
      } finally {
        setLoading(false)
      }
    }

    if (laneId) void load()
  }, [laneId])

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await api.lanes.update(laneId, {
        notes,
        status,
        targetCustomerPayCents: targetCustomerPayCents ? Number(targetCustomerPayCents) : null,
        targetCarrierPayCents: targetCarrierPayCents ? Number(targetCarrierPayCents) : null,
      })
      setItem(res.data.lane)
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to save lane')
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!confirm('Archive this lane?')) return
    try {
      await api.lanes.remove(laneId)
      navigate('/lanes')
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to archive lane')
    }
  }

  if (loading) return <div className="max-w-5xl mx-auto p-6 text-sm text-gray-500">Loading lane...</div>

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-4">
      <h1 className="text-2xl font-bold">Lane {item?.laneNumber}</h1>
      {error && <div className="border border-red-200 bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{error}</div>}

      <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div><span className="font-semibold">Customer:</span> {item?.customerName ?? '-'}</div>
        <div><span className="font-semibold">Status:</span> {item?.status ?? '-'}</div>
        <div>
          <span className="font-semibold">Route:</span>{' '}
          {[item?.originCity, item?.originState, item?.originZip].filter(Boolean).join(', ')}
          {' -> '}
          {[item?.destinationCity, item?.destinationState, item?.destinationZip].filter(Boolean).join(', ')}
        </div>
        <div><span className="font-semibold">Miles:</span> {item?.estDistanceMiles ?? '-'}</div>
        <div><span className="font-semibold">Customer Target:</span> {centsToDollars(item?.targetCustomerPayCents)}</div>
        <div><span className="font-semibold">Carrier Target:</span> {centsToDollars(item?.targetCarrierPayCents)}</div>
      </div>

      <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <select className="border rounded px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="ACTIVE">ACTIVE</option>
          <option value="PAUSED">PAUSED</option>
          <option value="ARCHIVED">ARCHIVED</option>
        </select>
        <input className="border rounded px-3 py-2 text-sm" value={targetCustomerPayCents} onChange={(e) => setTargetCustomerPayCents(e.target.value.replace(/[^0-9]/g, ''))} placeholder="Customer pay cents" />
        <input className="border rounded px-3 py-2 text-sm" value={targetCarrierPayCents} onChange={(e) => setTargetCarrierPayCents(e.target.value.replace(/[^0-9]/g, ''))} placeholder="Carrier pay cents" />
        <textarea className="md:col-span-2 border rounded px-3 py-2 text-sm" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Lane notes" />

        <div className="md:col-span-2 flex gap-2">
          <button onClick={save} disabled={saving} className="bg-blue-700 text-white px-4 py-2 rounded text-sm">{saving ? 'Saving...' : 'Save Lane'}</button>
          <button onClick={remove} className="border border-red-300 text-red-700 px-4 py-2 rounded text-sm">Archive Lane</button>
        </div>
      </div>

      <div className="bg-white border rounded-xl p-4 space-y-2">
        <h2 className="font-semibold">Price History</h2>
        {!history.length && <div className="text-sm text-gray-500">No pricing changes recorded.</div>}
        {history.map((entry) => (
          <div key={entry.id} className="border rounded px-3 py-2 text-sm">
            <div className="font-medium">{new Date(entry.created_at).toLocaleString()}</div>
            <div className="text-gray-600">
              Customer {centsToDollars(entry.previous_customer_pay_cents)} to {centsToDollars(entry.next_customer_pay_cents)}
              {' • '}
              Carrier {centsToDollars(entry.previous_carrier_pay_cents)} to {centsToDollars(entry.next_carrier_pay_cents)}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border rounded-xl p-4 space-y-2">
        <h2 className="font-semibold">VIN Inventory Assigned To Lane</h2>
        {!vinItems.length && <div className="text-sm text-gray-500">No VIN inventory currently assigned to this lane.</div>}
        {vinItems.map((vinItem) => (
          <div key={vinItem.id} className="border rounded px-3 py-2 text-sm">
            <div className="font-mono font-medium">{vinItem.vin}</div>
            <div className="text-gray-600">{[vinItem.year, vinItem.make, vinItem.model].filter(Boolean).join(' ') || '—'}</div>
            <div className="text-xs text-gray-500">{vinItem.inventoryStatus} • Decode: {vinItem.decodeStatus} • Customer: {vinItem.customerName || '-'}</div>
          </div>
        ))}
      </div>
    </div>
  )
}
